<template>
  <div id="app">
    <!-- Debug mode is set in .env -->
    <template v-if="error">
      <err :error="error" />
    </template>
    <template v-else-if="loading">
      <loading-icon />
    </template>
    <template v-else-if="hasIntents">
      <select-intent-tag @resize="updateSize" @save="save" />
      <selected-tags type="intents" @resize="updateSize" @save="save" />

      <select-enrichments @resize="updateSize" @save="save" v-if="hasEnrichments" />
      <selected-tags type="enrichments" @resize="updateSize" @save="save" v-if="hasEnrichments" />
    </template>
  </div>
</template>

<script>
/*global CustomElement*/
import { mapGetters } from 'vuex';
import Err from './components/Err.vue';
import SelectIntentTag from './components/SelectIntentTag.vue';
import SelectEnrichments from './components/SelectEnrichments.vue';
import SelectedTags from './components/SelectedTags.vue';
import { validateOptimizeConfig } from './api';
import LoadingIcon from './components/LoadingIcon.vue';

export default {
  name: 'App',

  components: {
    SelectIntentTag,
    SelectEnrichments,
    SelectedTags,
    Err,
    LoadingIcon,
  },

  data() {
    return {
      element: {},
      fieldContext: {},
      fieldValue: null,
    };
  },

  computed: {
    ...mapGetters({
      intentsToSave: 'getIntentsToSave',
      error: 'getError',
      debug: 'getDebug',
      hasEnrichments: 'hasEnrichments',
      loading: 'getLoading',
      hasIntents: 'hasIntents',
    }),
  },

  created() {
    try {
      CustomElement.init(this.initialize);
      CustomElement.onDisabledChanged(this.handleDisable);
    } catch (error) {
      this.$store.commit('setError', error);
    }

    // local debug
    if (this.debug) {
      // initialize the app with some sample config and state
      this.initialize(
        {
          config: {
            // We allow setting api key via env var for local dev/debugging, but otherwise expect
            // users to specify their api key within Kentico Kontent.
            optimizeApiKey: process.env.VUE_APP_OPTIMIZE_API_KEY,
          },
          value: '{"intents":{"dev":{"str":50}}}',
        },
        {}
      );
    }
  },

  methods: {
    async initialize(element, context) {
      this.element = element;
      this.fieldContext = context;
      this.fieldValue = this.element.value ? JSON.parse(this.element.value) : null;

      const optimizeConfig = {
        // By default, we don't expect users to specify the api host within Kentico Kontent and
        // instead we use an env var for the hosted Kontent app.
        // However, we _allow_ users to override the env var via Kentico config.
        optimizeApiHost: this.element.config?.optimizeApiHost || process.env.VUE_APP_OPTIMIZE_HOST,
        optimizeApiKey: this.element.config?.optimizeApiKey,
        optimizeProjectId: this.element.config?.optimizeProjectId || process.env.VUE_APP_OPTIMIZE_PROJECT_ID,
      };

      const { error: validationError } = validateOptimizeConfig(optimizeConfig);
      if (validationError) {
        this.$store.commit('setError', validationError);
        return;
      }

      this.$store.commit('setOptimizeConfig', optimizeConfig);
      this.$store.commit('setInitialSelection', this.fieldValue);
      await this.$store.dispatch('getDataFromUniform');

      this.updateSize();
    },

    // TODO: handle disabled state of the whole field
    handleDisable(disableState) {
      this.element.disabled = disableState;
    },

    save() {
      // Do not save in debug mode. We are not in the iframe.
      if (this.debug) {
        return false;
      }

      this.element.value = JSON.stringify(this.intentsToSave);
      CustomElement.setValue(JSON.stringify(this.intentsToSave));
    },

    updateSize() {
      // Do not resize in debug mode. We are not in the iframe.
      if (this.debug) {
        return false;
      }

      this.$nextTick(() => {
        const height = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight
        );

        CustomElement.setHeight(height);
      });
    },
  },
};
</script>
